import React from "react";
import { Link } from "react-router-dom";
import MarketingPageLayout, { PolicySection } from "@/components/marketing/MarketingPageLayout";

export default function DataRetentionPolicy() {
  return (
    <MarketingPageLayout
      eyebrow="Compliance"
      title="Data Retention Policy"
      subtitle="How long GuestIntel keeps personal data, when records are anonymised, and what survives anonymisation."
      lastUpdated="February 2026"
      path="/data-retention"
      seoDescription="GuestIntel retention horizons by data type, anonymisation mechanics, early-erasure options, and audit/verification practices."
    >
      <PolicySection title="1. Retention horizons">
        <table className="w-full text-sm border border-zinc-200 rounded-md overflow-hidden">
          <thead className="bg-zinc-50 text-left">
            <tr>
              <th className="px-3 py-2 font-semibold">Data type</th>
              <th className="px-3 py-2 font-semibold">Default retention</th>
              <th className="px-3 py-2 font-semibold">Notes</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-200">
            <tr>
              <td className="px-3 py-2">Operator account data</td>
              <td className="px-3 py-2">Life of tenant + 90 days</td>
              <td className="px-3 py-2">Anonymised after grace period.</td>
            </tr>
            <tr>
              <td className="px-3 py-2">Incident records — open / investigating</td>
              <td className="px-3 py-2">36 months</td>
              <td className="px-3 py-2">PII encrypted at rest, hashed for Intel Shield matching.</td>
            </tr>
            <tr>
              <td className="px-3 py-2">Incident records — resolved / closed</td>
              <td className="px-3 py-2">24 months</td>
              <td className="px-3 py-2">Auto-anonymised after horizon — see &sect; 2.</td>
            </tr>
            <tr>
              <td className="px-3 py-2">Audit logs</td>
              <td className="px-3 py-2">7 years</td>
              <td className="px-3 py-2">Required for regulatory and compliance audits.</td>
            </tr>
            <tr>
              <td className="px-3 py-2">Appeal records</td>
              <td className="px-3 py-2">5 years</td>
              <td className="px-3 py-2">Linked to the appealed incident lifecycle.</td>
            </tr>
            <tr>
              <td className="px-3 py-2">Marketing &amp; founding-program leads</td>
              <td className="px-3 py-2">24 months from last interaction</td>
              <td className="px-3 py-2">Or as soon as consent is withdrawn.</td>
            </tr>
          </tbody>
        </table>
      </PolicySection>

      <PolicySection title="2. Anonymisation mechanics">
        <p>
          When a record passes its retention horizon, our scheduled retention job replaces all direct
          identifiers (name, email, phone, ID number, photos) with anonymised placeholders. The structural
          record (category, severity, month, damage cost) survives so aggregated analytics stay accurate,
          but no individual can be re-identified.
        </p>
        <p>
          Hashed identifier columns (Intel Shield) are wiped at the same time, breaking any future
          cross-reference.
        </p>
      </PolicySection>

      <PolicySection title="3. Early erasure on request">
        <p>
          Guests can request earlier erasure via the <Link to="/gdpr">GDPR Rights portal</Link>. We honour
          valid requests within 30 days unless we have a documented legal obligation to keep the data
          longer.
        </p>
      </PolicySection>

      <PolicySection title="4. Operator-controlled retention">
        <p>
          Enterprise tenants can shorten retention for their own records below the defaults above (but not
          extend it). Configurable from <Link to="/app/settings">Settings → Data &amp; retention</Link>.
        </p>
      </PolicySection>

      <PolicySection title="5. Audit & verification">
        <p>
          Every anonymisation pass writes an audit-log entry. We publish quarterly anonymisation counts
          on the <Link to="/trust">Trust Center</Link>.
        </p>
      </PolicySection>
    </MarketingPageLayout>
  );
}
